import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import ItemCard from '../components/ItemCard';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Skeleton } from '../components/ui/skeleton';
import { SlidersHorizontal, Heart, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";

const GalleryPage = () => {
    const { t } = useLanguage();
    const { api, user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    const [items, setItems] = useState([]);
    const [currentEvent, setCurrentEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    
    const ITEMS_PER_PAGE = 12;
    
    const [filters, setFilters] = useState({
        category: searchParams.get('category') || '',
    });

    const categories = ['coat', 'dress', 'top', 'pants', 'shoes', 'accessories', 'other'];

    const fetchData = useCallback(async (loadMore = false) => {
        try {
            if (loadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }
            
            const eventRes = await api().get('/events/current');
            setCurrentEvent(eventRes.data);

            const params = new URLSearchParams();
            if (eventRes.data?.id) params.append('event_id', eventRes.data.id);
            if (filters.category) params.append('category', filters.category);
            
            const offset = loadMore ? items.length : 0;
            params.append('limit', ITEMS_PER_PAGE);
            params.append('offset', offset);

            const itemsRes = await api().get(`/items/?${params.toString()}`);
            const newItems = itemsRes.data || [];
            
            if (loadMore) {
                setItems(prev => [...prev, ...newItems]);
            } else {
                setItems(newItems);
            }
            
            setHasMore(newItems.length === ITEMS_PER_PAGE);
        } catch (error) {
            console.error('Failed to fetch gallery:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [api, filters, items.length]);

    useEffect(() => {
        fetchData(false);
    }, [filters]);

    const handleFilterChange = (key, value) => {
        const actualValue = value === 'all' ? '' : value;
        const newFilters = { ...filters, [key]: actualValue };
        setFilters(newFilters);
        
        const params = new URLSearchParams();
        Object.entries(newFilters).forEach(([k, v]) => {
            if (v) params.set(k, v);
        });
        setSearchParams(params);
    };

    const clearFilters = () => {
        setFilters({ category: '' });
        setSearchParams({});
    };

    const hasActiveFilters = Object.values(filters).some(v => v);

    const handleItemClick = async (item) => {
        try {
            const response = await api().get(`/items/${item.id}`);
            setSelectedItem(response.data);
        } catch (error) {
            toast.error('Failed to load item details');
        }
    };

    const handleReaction = async (itemId, type = 'yess') => {
        setActionLoading(true);
        try {
            if (selectedItem?.user_reaction) {
                await api().delete(`/reactions/${itemId}`);
            } else {
                await api().post('/reactions/', { item_id: itemId, type });
            }
            await fetchData();
            if (selectedItem) {
                const response = await api().get(`/items/${itemId}`);
                setSelectedItem(response.data);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update reaction');
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white pb-24">
            {/* Fixed Header */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 safe-area-top">
                <div className="flex items-center justify-between px-4 py-4">
                    <button
                        onClick={() => navigate('/menu?section=yess-items')}
                        className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <img 
                        src={LOGO_URL} 
                        alt="Galeries Yess" 
                        className="h-8 w-auto"
                    />
                    <div className="w-9" />
                </div>
            </div>

            <main className="pt-20 px-4">
                {/* Page Title */}
                <h1 className="text-2xl font-heading font-light tracking-tight mb-6">
                    Yess Items
                </h1>

                {/* Filter Bar */}
                <div className="flex items-center gap-3 mb-8 overflow-x-auto pb-2">
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className={`shrink-0 bg-white border-gray-200 ${hasActiveFilters ? 'border-black' : ''}`}
                                data-testid="filter-button"
                            >
                                <SlidersHorizontal className="w-4 h-4 mr-2" />
                                Filters
                                {hasActiveFilters && (
                                    <span className="ml-1 w-5 h-5 rounded-full bg-black text-white text-xs flex items-center justify-center">
                                        {Object.values(filters).filter(v => v).length}
                                    </span>
                                )}
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="bottom" className="h-auto bg-white">
                            <SheetHeader>
                                <SheetTitle className="font-heading">Filters</SheetTitle>
                            </SheetHeader>
                            <div className="space-y-6 py-8">
                                <div>
                                    <label className="text-sm text-muted-foreground mb-2 block">{t('category')}</label>
                                    <Select
                                        value={filters.category || 'all'}
                                        onValueChange={(v) => handleFilterChange('category', v)}
                                    >
                                        <SelectTrigger className="bg-white" data-testid="filter-category">
                                            <SelectValue placeholder="All categories" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="all">All</SelectItem>
                                            {categories.map(cat => (
                                                <SelectItem key={cat} value={cat}>{t(cat)}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {hasActiveFilters && (
                                    <Button
                                        variant="outline"
                                        onClick={clearFilters}
                                        className="w-full"
                                        data-testid="clear-filters"
                                    >
                                        {t('clearFilters')}
                                    </Button>
                                )}
                            </div>
                        </SheetContent>
                    </Sheet>

                    {/* Quick filter pills */}
                    {categories.slice(0, 4).map(cat => (
                        <Button
                            key={cat}
                            variant={filters.category === cat ? 'default' : 'outline'}
                            size="sm"
                            className={`shrink-0 rounded-full ${filters.category === cat ? 'bg-[#F8BBD6] text-foreground border-[#F8BBD6]' : 'bg-white'}`}
                            onClick={() => handleFilterChange('category', filters.category === cat ? '' : cat)}
                            data-testid={`quick-filter-${cat}`}
                        >
                            {t(cat)}
                        </Button>
                    ))}
                </div>

                {/* Items Grid */}
                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {[...Array(8)].map((_, i) => (
                            <Skeleton key={i} className="aspect-[3/4] w-full bg-white" />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-muted-foreground font-accent italic text-lg">{t('noItems')}</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {items.map((item, index) => (
                                <div
                                    key={item.id}
                                    className="animate-fade-in"
                                    style={{ animationDelay: `${index * 0.05}s` }}
                                >
                                    <ItemCard item={item} onClick={handleItemClick} />
                                </div>
                            ))}
                        </div>
                        
                        {/* Load More Button */}
                        {hasMore && (
                            <div className="flex justify-center mt-8 mb-4">
                                <Button
                                    variant="outline"
                                    onClick={() => fetchData(true)}
                                    disabled={loadingMore}
                                    className="rounded-full px-8"
                                    data-testid="load-more-button"
                                >
                                    {loadingMore ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : null}
                                    {loadingMore ? 'Loading...' : 'Load more'}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* Item Detail Sheet */}
            <Sheet open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
                <SheetContent side="bottom" className="h-[90vh] overflow-y-auto bg-white">
                    {selectedItem && (
                        <div className="pb-24">
                            {/* Images Carousel */}
                            {(() => {
                                const allPhotos = selectedItem.photos_original || [];
                                const photos = allPhotos.length > 0 ? allPhotos : [selectedItem.photo_processed];
                                return photos.length > 1 ? (
                                    <div className="relative">
                                        <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide">
                                            {photos.map((photo, index) => (
                                                <div key={index} className="flex-shrink-0 w-full snap-center">
                                                    <div className="aspect-square w-full max-w-md mx-auto overflow-hidden bg-white border border-border/30">
                                                        <img
                                                            src={photo}
                                                            alt={`${selectedItem.title} ${index + 1}`}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        {/* Dots indicator */}
                                        <div className="flex justify-center gap-1 mt-2 mb-4">
                                            {photos.map((_, index) => (
                                                <div key={index} className="w-2 h-2 rounded-full bg-gray-300" />
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="aspect-square w-full max-w-md mx-auto mb-8 overflow-hidden bg-white border border-border/30">
                                        <img
                                            src={selectedItem.photo_processed || photos[0]}
                                            alt={selectedItem.title}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                );
                            })()}

                            {/* Info */}
                            <div className="space-y-6 px-2">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h2 className="text-2xl font-heading font-semibold">{selectedItem.title}</h2>
                                        <p className="text-muted-foreground font-mono text-sm uppercase">
                                            {t(selectedItem.category)} · {selectedItem.size_text || 'One size'}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('startingBid')}</p>
                                        <span className="text-2xl font-mono">€{selectedItem.price?.toFixed(0)}</span>
                                    </div>
                                </div>

                                {/* Details */}
                                {(selectedItem.brand || selectedItem.materials_text || selectedItem.care_text) && (
                                    <div className="space-y-3 py-6 border-y border-border/30">
                                        {selectedItem.brand && (
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">{t('itemBrand')}</span>
                                                <span className="font-medium">{selectedItem.brand}</span>
                                            </div>
                                        )}
                                        {selectedItem.materials_text && (
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">{t('itemMaterials')}</span>
                                                <span className="text-right max-w-[60%]">{selectedItem.materials_text}</span>
                                            </div>
                                        )}
                                        {selectedItem.care_text && (
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">{t('itemCare')}</span>
                                                <span className="text-right max-w-[60%]">{selectedItem.care_text}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Owner */}
                                {selectedItem.owner && (
                                    <div className="flex items-center gap-3 py-2">
                                        <div className="w-10 h-10 rounded-full bg-[#F8BBD6]/30 flex items-center justify-center">
                                            <span className="font-medium text-foreground">
                                                {selectedItem.owner.name?.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-medium">{selectedItem.owner.name}</p>
                                            <p className="text-xs text-muted-foreground">Donated by</p>
                                        </div>
                                    </div>
                                )}

                                {/* Stats */}
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                        <Heart className={`w-4 h-4 ${selectedItem.user_reaction === 'yess' ? 'fill-[#F8BBD6] text-[#F8BBD6]' : ''}`} />
                                        {selectedItem.yess_count || 0} {t('yess')}
                                    </span>
                                </div>

                                {/* Live Auction Notice */}
                                {selectedItem.status === 'available' && (
                                    <div className="bg-gray-50 border border-gray-200 px-4 py-4 rounded-lg">
                                        <p className="text-sm text-gray-600 text-center italic">
                                            This item will be auctioned live at the event. Bidding is in-person only.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Actions - Fixed at bottom */}
                            {selectedItem.status === 'available' && selectedItem.owner_user_id !== user?.id && (
                                <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-border/30">
                                    <div className="flex items-center justify-center max-w-lg mx-auto">
                                        {/* Yess Button - Interest indicator only */}
                                        <Button
                                            variant={selectedItem.user_reaction === 'yess' ? 'default' : 'outline'}
                                            size="lg"
                                            onClick={() => handleReaction(selectedItem.id)}
                                            disabled={actionLoading}
                                            className={`px-8 ${selectedItem.user_reaction === 'yess' ? 'bg-[#F8BBD6] border-[#F8BBD6] hover:bg-[#f0a8c8]' : ''}`}
                                            data-testid="yess-button"
                                        >
                                            {actionLoading ? (
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                            ) : (
                                                <>
                                                    <Heart className={`w-5 h-5 mr-2 ${selectedItem.user_reaction === 'yess' ? 'fill-current' : ''}`} />
                                                    {selectedItem.user_reaction === 'yess' ? 'Yess!' : t('yess')}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {selectedItem.status === 'sold' && (
                                <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-border/30">
                                    <div className="text-center text-muted-foreground font-medium">
                                        {t('sold')}
                                    </div>
                                </div>
                            )}

                            {selectedItem.status === 'unsold' && (
                                <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-border/30">
                                    <div className="text-center text-muted-foreground font-medium">
                                        Unsold
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
};

export default GalleryPage;
